import {useContext, useEffect} from 'react';
import {NavigationFocusContext} from '@header/scripts/components/context/NavigationFocusContext.jsx';
import whatInput from 'what-input';

const SECOND_NAVI_LEVEL = 'second';

export const useNaviFocusTrap = (containerRef, level, closeLayer, disableFocusTrap = false) => {
    const {navigationFocus, setFocusedLevel, setFocusFirst, reset} = useContext(NavigationFocusContext);

    const focusOut = () => {
        closeLayer();
        reset();
    };

    useEffect(() => {
        if (disableFocusTrap) return;

        const inputType = whatInput.ask('intent');
        if (inputType !== 'keyboard') return;

        if (navigationFocus.focusedLevel !== level) return;

        const container = containerRef.current;
        if (!container) return;

        const focusables = getFocusableElements(container);
        if (navigationFocus.focusFirst) {
            focusables[0]?.focus();
            setFocusFirst(false);
        }

        function handleKeyDown(e) {
            if (e.key !== 'Tab') return;

            const active = document.activeElement;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const triggerElement =
                level === SECOND_NAVI_LEVEL
                    ? navigationFocus.firstLevelTriggerElement
                    : navigationFocus.secondLevelTriggerElement;

            if (e.shiftKey && active === first) {
                e.preventDefault();
                triggerElement?.focus();
                if (level === SECOND_NAVI_LEVEL) {
                    focusOut();
                }
                setFocusedLevel(SECOND_NAVI_LEVEL);
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                focusOut();
                focusNextOutside(navigationFocus.firstLevelTriggerElement);
            }
        }

        container.addEventListener('keydown', handleKeyDown);
        return () => container.removeEventListener('keydown', handleKeyDown);
    }, [navigationFocus, closeLayer]);
};

function getFocusableElements(container) {
    if (!container) return [];
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    return Array.from(container.querySelectorAll(focusableSelectors)).filter(
        (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'),
    );
}

function focusNextOutside(startingElement) {
    const all = getFocusableElements(document.body);
    const index = all.indexOf(startingElement);
    if (index !== -1 && all[index + 1]) {
        all[index + 1].focus();
    }
}
